import type { AppSettings } from "../app/AppSettings.js";
import {
  BGM_CUES,
  SE_CUES,
  type BgmCueId,
  type SeCueId,
} from "./AudioCue.js";

interface PlayingEffect {
  audio: HTMLAudioElement;
  gain: number;
}

export interface AudioDebugState {
  unlocked: boolean;
  desiredBgm: BgmCueId | null;
  currentBgm: BgmCueId | null;
  currentBgmPaused: boolean;
  bgmPlayAttempts: number;
  sePlayAttempts: number;
  recentSe: SeCueId[];
  effectiveBgmVolume: number;
  lastError: string | null;
}

export class AudioDirector {
  private settings: AppSettings;
  private unlocked = false;
  private desiredBgm: BgmCueId | null = null;
  private currentBgm: { cue: BgmCueId; audio: HTMLAudioElement } | null = null;
  private readonly effects = new Set<PlayingEffect>();
  private fadeTimer: number | null = null;
  private bgmPlayAttempts = 0;
  private sePlayAttempts = 0;
  private readonly recentSe: SeCueId[] = [];
  private lastError: string | null = null;

  public constructor(initialSettings: AppSettings) {
    this.settings = initialSettings;
    window.addEventListener("pointerdown", this.handleInteraction, true);
    window.addEventListener("keydown", this.handleInteraction, true);
  }

  public get debugState(): AudioDebugState {
    return {
      unlocked: this.unlocked,
      desiredBgm: this.desiredBgm,
      currentBgm: this.currentBgm?.cue ?? null,
      currentBgmPaused: this.currentBgm?.audio.paused ?? true,
      bgmPlayAttempts: this.bgmPlayAttempts,
      sePlayAttempts: this.sePlayAttempts,
      recentSe: [...this.recentSe],
      effectiveBgmVolume: this.currentBgm?.audio.volume ?? 0,
      lastError: this.lastError,
    };
  }

  public updateSettings(settings: AppSettings): void {
    this.settings = settings;
    if (this.currentBgm) {
      const definition = BGM_CUES[this.currentBgm.cue];
      this.currentBgm.audio.volume = this.bgmVolume(definition.gain);
    }
    for (const effect of this.effects) {
      effect.audio.volume = this.seVolume(effect.gain);
    }
  }

  public setBgm(cue: BgmCueId | null): void {
    this.desiredBgm = cue;
    if (!cue) {
      this.stopBgm();
      return;
    }
    if (!this.unlocked) return;
    void this.startBgm(cue);
  }

  public playSe(cue: SeCueId): void {
    const definition = SE_CUES[cue];
    const audio = new Audio(definition.url);
    const effect = { audio, gain: definition.gain };
    audio.preload = "auto";
    audio.volume = this.seVolume(definition.gain);
    this.effects.add(effect);
    this.sePlayAttempts += 1;
    this.recentSe.push(cue);
    if (this.recentSe.length > 16) this.recentSe.shift();

    const release = (): void => {
      this.effects.delete(effect);
      audio.removeEventListener("ended", release);
      audio.removeEventListener("error", release);
    };
    audio.addEventListener("ended", release);
    audio.addEventListener("error", release);
    void audio.play().catch((error: unknown) => {
      this.lastError = error instanceof Error ? error.message : String(error);
      release();
    });
  }

  public destroy(): void {
    window.removeEventListener("pointerdown", this.handleInteraction, true);
    window.removeEventListener("keydown", this.handleInteraction, true);
    this.stopBgm();
    for (const effect of this.effects) {
      effect.audio.pause();
      effect.audio.removeAttribute("src");
    }
    this.effects.clear();
  }

  private readonly handleInteraction = (): void => {
    this.unlocked = true;
    if (this.desiredBgm) void this.startBgm(this.desiredBgm);
  };

  private async startBgm(cue: BgmCueId): Promise<void> {
    if (this.currentBgm?.cue === cue) {
      if (this.currentBgm.audio.paused) await this.tryPlay(this.currentBgm.audio);
      return;
    }

    const definition = BGM_CUES[cue];
    const previous = this.currentBgm?.audio ?? null;
    const next = new Audio(definition.url);
    next.loop = Boolean(definition.loop);
    next.preload = "auto";
    next.volume = 0;
    this.currentBgm = { cue, audio: next };
    this.cancelFade();
    await this.tryPlay(next);
    if (this.currentBgm?.audio !== next) {
      next.pause();
      return;
    }
    this.crossfade(previous, next, cue);
  }

  private async tryPlay(audio: HTMLAudioElement): Promise<void> {
    this.bgmPlayAttempts += 1;
    try {
      await audio.play();
      this.lastError = null;
    } catch (error: unknown) {
      this.lastError = error instanceof Error ? error.message : String(error);
    }
  }

  private crossfade(
    previous: HTMLAudioElement | null,
    next: HTMLAudioElement,
    cue: BgmCueId,
  ): void {
    const previousVolume = previous?.volume ?? 0;
    const startedAt = performance.now();
    const duration = 420;

    const tick = (): void => {
      const progress = Math.min(1, (performance.now() - startedAt) / duration);
      if (this.currentBgm?.audio !== next) {
        previous?.pause();
        this.cancelFade();
        return;
      }
      const targetVolume = this.bgmVolume(BGM_CUES[cue].gain);
      next.volume = targetVolume * progress;
      if (previous) previous.volume = previousVolume * (1 - progress);
      if (progress < 1) return;
      previous?.pause();
      if (previous) previous.currentTime = 0;
      this.cancelFade();
    };

    tick();
    this.fadeTimer = window.setInterval(tick, 30);
  }

  private stopBgm(): void {
    this.cancelFade();
    if (!this.currentBgm) return;
    this.currentBgm.audio.pause();
    this.currentBgm.audio.currentTime = 0;
    this.currentBgm = null;
  }

  private cancelFade(): void {
    if (this.fadeTimer === null) return;
    window.clearInterval(this.fadeTimer);
    this.fadeTimer = null;
  }

  private bgmVolume(gain: number): number {
    return clamp01(this.settings.masterVolume / 100 * this.settings.bgmVolume / 100 * gain);
  }

  private seVolume(gain: number): number {
    return clamp01(this.settings.masterVolume / 100 * this.settings.seVolume / 100 * gain);
  }
}

function clamp01(value: number): number {
  return Math.max(0, Math.min(1, value));
}
